/**
 * clasificadores/gatePromoRentabilidad.js — GATE de rentabilidad para
 * sugerir promociones (decisión Mariano 12-jul, Estrategia Full).
 *
 * Regla: antes de sugerir una promo hay que revisar que la rentabilidad del
 * producto esté por encima de la rentabilidad mínima requerida por el cliente.
 *   - Sin costo o sin corte cargados → se sugiere igual, con advertencia
 *     ("validá rentabilidad antes de aplicar el descuento").
 *   - Con datos y margen actual > corte → se sugiere CON números: margen
 *     actual, corte, descuento máximo y precio piso (costo ÷ (1 − corte)).
 *   - Con datos y margen actual ≤ corte → NO se sugiere (sin espacio).
 *
 * El corte se resuelve por la cascada ADR 032 (producto → categoría → marca →
 * proveedor → empresa) — `construirInfoRentabilidadPorItem` la arma desde las
 * colecciones crudas para que TODOS los clasificadores (Full, Matriz,
 * Velocidad, CentralPromos) usen exactamente el mismo criterio.
 *
 * PURO — sin Firestore. Consumido por clasificadores y vistas 🎯 (paridad).
 */

import { resolverRentabilidadMinima } from '@core/lib/rentabilidadMinima'
import { gatePromoInferido, resumenAdsPublicacion, margenParaVeto, resolverBenchmarksML } from '@core/lib/rentabilidadEstimada'

/**
 * Evalúa el gate para un item.
 * @param {Object} args
 * @param {number} args.precio     — precio actual de la publicación
 * @param {number} args.costoUnit  — costo promedio del producto vinculado
 * @param {number} args.corte      — rentabilidad mínima resuelta (fracción 0–1)
 * @returns {{estado: 'sin_datos'|'con_espacio'|'sin_espacio', margenActualPct: number|null,
 *            cortePct: number|null, espacioPct: number|null, descuentoMaxPct: number|null,
 *            precioPiso: number|null}}
 */
export function gatePromoRentabilidad({ precio, costoUnit, corte } = {}) {
  const p = Number(precio)
  const c = Number(costoUnit)
  const m = Number(corte)
  const conPrecioYCosto = p > 0 && c > 0
  const conCorte = Number.isFinite(m) && m > 0 && m < 1

  if (!conPrecioYCosto || !conCorte) {
    return {
      estado: 'sin_datos',
      margenActualPct: conPrecioYCosto ? (p - c) / p : null,
      cortePct: conCorte ? m : null,
      espacioPct: null,
      descuentoMaxPct: null,
      precioPiso: null,
    }
  }

  const margenActualPct = (p - c) / p
  if (margenActualPct <= m) {
    return { estado: 'sin_espacio', margenActualPct, cortePct: m, espacioPct: margenActualPct - m, descuentoMaxPct: null, precioPiso: null }
  }

  // Precio piso: el precio al que el margen queda EXACTO en el corte.
  //   margen(piso) = (piso − costo) / piso = corte  →  piso = costo / (1 − corte)
  const precioPiso = c / (1 - m)
  const descuentoMaxPct = Math.max(0, 1 - precioPiso / p)
  return { estado: 'con_espacio', margenActualPct, cortePct: m, espacioPct: margenActualPct - m, descuentoMaxPct, precioPiso }
}

/** Texto corto del gate para el _motivo / descripción de la tarea. */
export function textoGate(gate) {
  if (!gate) return ''
  const pct = (v) => (v == null || !isFinite(v) ? '—' : `${(v * 100).toFixed(1)}%`)
  if (gate.estado === 'con_espacio') {
    return `margen ${pct(gate.margenActualPct)} · mínimo ${pct(gate.cortePct)} — espacio para descuento de hasta ${pct(gate.descuentoMaxPct)} (precio piso $${Math.round(gate.precioPiso).toLocaleString('es-AR')})`
  }
  if (gate.estado === 'sin_espacio') {
    if (gate.motivo === 'ads_gastan_sin_vender') {
      return 'sin espacio: la pauta gasta sin generar ninguna venta — corresponde revisar/pausar el ad, no sumar promo'
    }
    return `sin espacio: margen ${pct(gate.margenActualPct)} ≤ mínimo ${pct(gate.cortePct)}`
  }
  return '⚠ sin costo/margen mínimo cargados — validá rentabilidad antes de aplicar el descuento'
}

/**
 * Índice item_id → bloque `metrics` de los ads legacy (adsItems del loader
 * mlAds). OJO: varios ads del mismo item comparten EL MISMO bloque de
 * métricas (el join canónico indexa por item) — se toma UNA sola vez por
 * item para no duplicar el gasto.
 */
export function metricasAdsPorItem(adsItems = []) {
  const m = new Map()
  for (const ad of adsItems || []) {
    const itemId = ad?.item_id || ad?.itemId
    if (!itemId) continue
    const k = String(itemId)
    if (!m.has(k) && ad?.metrics && Object.keys(ad.metrics).length > 0) m.set(k, ad.metrics)
  }
  return m
}

/**
 * GATE de promos con COSTOS REALES (15-jul-2026) — el reemplazo del gate
 * bruto en Matriz/Velocidad/Full. Misma interfaz de estados; por dentro:
 *
 *   - fórmula COMPLETA inferida (comisión/envío por cascada cuenta → eco +
 *     impuestos de la config fiscal) en lugar de (precio − costo) / precio
 *   - cobra el GASTO REAL de ads de la pub (decisión Mariano: "la renta de
 *     hoy carga la plata que salió de verdad"): gasto ÷ facturación total de
 *     la pub en la ventana (TACOS); sin ventas totales, gasto ÷ atribuidas
 *   - gastó en ads y no vendió NADA → sin_espacio con motivo
 *     'ads_gastan_sin_vender' (candidata a pausar_ad, jamás a promo)
 *
 * Los ads se juzgan aparte con ACOS puro — acá solo se reparte el gasto.
 *
 * @param {Object} args
 * @param {number} args.precio        — precio actual de la publicación
 * @param {Object} args.rent          — { costoUnit, corte } de construirInfoRentabilidadPorItem
 * @param {Object} args.metricsAds    — bloque metrics del ad de la pub (metricasAdsPorItem)
 * @param {Object} args.ventasItem    — { unidades, facturacion } de la pub en la ventana (null si no vendió)
 * @param {string} args.categoryId    — categoría ML (pub.datosCanal.categoryId)
 * @param {Object} args.estructuraCuenta — estructuraCostosML(ventas30d)
 * @param {Object} args.ecosistema    — doc conocimientoEcosistema/costos-ml
 * @param {Object} args.impuestosEmpresa — config fiscal mergeada (loader _impuestos)
 */
export function gatePromoConCostosReales({
  precio, rent = null, metricsAds = null, ventasItem = null,
  categoryId = null, estructuraCuenta = null, ecosistema = null, impuestosEmpresa = null,
} = {}) {
  const p = Number(precio)
  const ads = metricsAds ? resumenAdsPublicacion([{ metricas: metricsAds }]) : null
  let adUnit = 0
  let gastoAdsSinVentas = false
  if (ads && p > 0) {
    const fact = Number(ventasItem?.facturacion) || 0
    if (fact > 0) adUnit = (ads.cost / fact) * p               // TACOS: gasto real ÷ ventas totales
    else if (ads.amount > 0) adUnit = (ads.cost / ads.amount) * p  // solo atribuidas (peor caso)
    else gastoAdsSinVentas = true                               // gastó y no vendió NADA
  }
  return gatePromoInferido({
    precio: p,
    costoUnit: rent?.costoUnit ?? null,
    corte: rent?.corte ?? null,
    categoryId, estructuraCuenta, ecosistema, impuestosEmpresa,
    adUnit, gastoAdsSinVentas,
  })
}

/**
 * Arma el mapa itemId → { productoId, costoUnit, corte } resolviendo la
 * cascada ADR 032 desde las colecciones crudas.
 *
 * @param {Object} args
 * @param {Array}  args.productos        — productos de la empresa (costoPromedio, rentabilidadMinima, categoriaId, marcaId)
 * @param {Array}  args.catalogoLinks    — vínculos {externalItemId, productoId, plataformaId}
 * @param {Array}  args.categorias       — {id, rentabilidadMinima}
 * @param {Array}  args.marcas           — {id, rentabilidadMinima}
 * @param {Array}  args.proveedores      — {id, rentabilidadMinima}
 * @param {Array}  args.productoSourcing — {productoId, proveedorId, preferido, activo}
 * @param {number|null} args.empresaMin  — empresas/{id}/config/rentabilidad.rentabilidadMinima
 * @returns {Map<string, {productoId: string, costoUnit: number|null, corte: number|null}>}
 */
export function construirInfoRentabilidadPorItem({
  productos = [],
  catalogoLinks = [],
  categorias = [],
  marcas = [],
  proveedores = [],
  productoSourcing = [],
  empresaMin = null,
} = {}) {
  const prodById = new Map()
  for (const p of productos) prodById.set(p.id, p)
  const minDe = (arr) => {
    const m = new Map()
    for (const e of arr) { if (e?.rentabilidadMinima != null) m.set(e.id, e.rentabilidadMinima) }
    return m
  }
  const catMin = minDe(categorias)
  const marcaMin = minDe(marcas)
  const provMin = minDe(proveedores)
  // Proveedor por producto: el preferido gana; el primer activo es fallback
  // (mismo criterio que cargarRentabilidadMinima en catalogoRollup).
  const provPorProducto = new Map()
  for (const s of productoSourcing) {
    if (!s?.productoId || !s?.proveedorId || s.activo === false) continue
    if (s.preferido || !provPorProducto.has(s.productoId)) provPorProducto.set(s.productoId, s.proveedorId)
  }

  const out = new Map()
  for (const link of catalogoLinks) {
    if (link.plataformaId && link.plataformaId !== 'mercado-libre') continue
    if (!link.externalItemId || !link.productoId) continue
    const prod = prodById.get(link.productoId)
    if (!prod) continue
    const costoRaw = Number(prod.costoPromedio)
    const corte = resolverRentabilidadMinima({
      producto: prod.rentabilidadMinima,
      categoria: catMin.get(prod.categoriaId),
      marca: marcaMin.get(prod.marcaId),
      proveedor: provMin.get(provPorProducto.get(prod.id)),
      empresa: empresaMin,
    })
    out.set(String(link.externalItemId), {
      productoId: prod.id,
      costoUnit: Number.isFinite(costoRaw) && costoRaw > 0 ? costoRaw : null,
      corte,
    })
  }
  return out
}


/**
 * GATE de ADS para los PRENDEDORES (15-jul-2026): antes de sugerir "prender
 * publicidad" se proyecta el margen con el ACOS ESPERADO.
 *
 *   margen base HOY (real → inferida → null, cascada margenParaVeto)
 *   − ACOS esperado (cuenta → eco-categoría → eco-global; la pub a prender
 *     no tiene pauta, así que no hay ACOS propio)
 *   = margen proyectado →
 *       < 0        → 'veto_perdida'  (prender es pagar por vender a pérdida)
 *       < corte    → 'sin_espacio'   (te deja abajo de la mínima — declarado)
 *       resto      → 'ok'
 *       sin margen → 'sin_datos'     (se sugiere con ⚠, no frenamos a ciegas)
 *
 * Los ads se juzgan con ACOS puro (decisión Mariano): margen − ACOS, sin
 * ajustes de impuestos sobre la pauta (viene sin IVA y no se toma crédito).
 */
export function gateAdsPrender({
  precio, margenReal = null, costoUnit = null, corte = null, categoryId = null,
  estructuraCuenta = null, ecosistema = null, impuestosEmpresa = null, benchmarksEmpresa = null,
} = {}) {
  const base = margenParaVeto({
    margenReal, precio, costoUnit,
    estructura: estructuraCuenta, categoryId, ecosistema, impuestosEmpresa,
  })
  if (!base || base.margenPct == null || !isFinite(base.margenPct)) {
    return { estado: 'sin_datos', margenBasePct: null, fuenteMargen: null, acosEsperadoPct: null, fuenteAcos: null, margenProyectadoPct: null, cortePct: null }
  }
  const bm = resolverBenchmarksML({ benchmarksEmpresa, ecosistema, categoryId })
  const acos = Number.isFinite(bm?.acosPct) ? bm.acosPct : null
  const margenProy = acos != null ? base.margenPct - acos : base.margenPct
  const corteNum = (corte == null || corte === '') ? null : Number(corte)
  let estado = 'ok'
  if (margenProy < 0) estado = 'veto_perdida'
  else if (Number.isFinite(corteNum) && margenProy < corteNum) estado = 'sin_espacio'
  return {
    estado,
    margenBasePct: base.margenPct,
    fuenteMargen: base.fuente,
    acosEsperadoPct: acos,
    fuenteAcos: bm?.fuenteAcos || null,
    margenProyectadoPct: margenProy,
    cortePct: Number.isFinite(corteNum) ? corteNum : null,
  }
}

/** Texto corto del gate de ads para el _motivo de la tarea. */
export function textoGateAds(g) {
  if (!g) return ''
  const pct = (v) => (v == null || !isFinite(v) ? '—' : (v * 100).toFixed(1) + '%')
  if (g.estado === 'sin_datos') return '⚠ sin costo/margen para proyectar la pauta — validá rentabilidad antes de prender'
  const f = g.fuenteAcos?.tipo === 'cuenta' ? 'tu cuenta' : (g.fuenteAcos ? 'ecosistema' : 'sin benchmark: sin ACOS proyectado')
  return `margen hoy ${pct(g.margenBasePct)} (${g.fuenteMargen}) − ACOS esperado ${pct(g.acosEsperadoPct)} (${f}) = ${pct(g.margenProyectadoPct)} proyectado`
}
